import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import CallbackForm from "../components/CallbackForm";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { findBlog, BLOGS } from "../lib/blogs";
import { Clock, ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";

export default function BlogDetail() {
  const { slug } = useParams();
  const post = findBlog(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOGS.filter((b) => b.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-800 font-semibold" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4"/> All articles
          </Link>
          <div className="mt-5">
            <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              {post.category}
            </span>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5"/>{post.author}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{post.published}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>{post.read_minutes} min read</span>
            </div>
          </div>

          {post.cover && (
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 aspect-[16/9] bg-slate-100">
              <img src={post.cover} alt={post.title} className="w-full h-full object-cover"/>
            </div>
          )}

          <div className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed">
            <p className="text-slate-600 text-base italic border-l-4 border-teal-500 pl-4">{post.excerpt}</p>
            {post.body.map((sec, i) => (
              <section key={i} className="mt-8">
                <h2 className="font-heading text-2xl font-semibold text-slate-900">{sec.h}</h2>
                <p className="mt-3 text-slate-700 leading-relaxed">{sec.p}</p>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-2xl bg-gradient-to-br from-teal-600 to-teal-700 text-white p-6 sm:p-8">
            <h3 className="font-heading text-xl sm:text-2xl font-semibold">Worried about your diagnosis?</h3>
            <p className="mt-2 text-white/90 text-sm sm:text-base">
              Upload your MRI / CT &amp; symptoms and get a written second opinion from a NIMHANS or AIIMS-trained specialist — in 48 hours, from ₹1,000.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/login?as=patient">
                <Button className="h-11 px-5 bg-white text-teal-700 hover:bg-slate-100 font-semibold" data-testid="blog-cta-get-opinion">
                  Get my second opinion <ArrowRight className="w-4 h-4 ml-1.5"/>
                </Button>
              </Link>
              <a href="https://wa.me/918317674160" target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-11 px-5 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
                  WhatsApp us
                </Button>
              </a>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-xl font-semibold text-slate-900">Keep reading</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group" data-testid={`blog-related-${r.slug}`}>
                    <Card className="overflow-hidden border-slate-200 lift h-full flex flex-col">
                      {r.cover && (
                        <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                          <img src={r.cover} alt={r.title} className="w-full h-full object-cover transition duration-500 group-hover:scale-105" loading="lazy"/>
                        </div>
                      )}
                      <div className="p-4">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">{r.category}</span>
                        <div className="mt-1 font-heading font-semibold text-base text-slate-900 line-clamp-2 group-hover:text-teal-700 transition">{r.title}</div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky sidebar with callback form */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-4">
            <CallbackForm source={`blog:${post.slug}`} />
          </div>
        </aside>
      </article>
    </div>
  );
}
